import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import Box from "@mui/material/Box"

import { useDispatch } from "react-redux";
import { withTranslation } from "react-i18next";
import { isEmpty, isNull, isUndefined, orderBy, replace, size, some, isEqual } from "lodash";

import purchaseOrderService from "@plannel/services/data/purchase-order-service";
import bodService from "@plannel/services/data/bod-service";

import PaginationContainer from "@plannel/components/PaginationContainer";
import Dialog from "@plannel/components/Dialog";
import ItemAutocomplete from "@plannel/components/filter/ItemAutocomplete";
import ExcelExportButton from "@plannel/components/ExcelExportButton";
import Snackbar from "@plannel/components/Snackbar";
import DataState from "@plannel/components/aggrid/DataState";
import DefaultGridSetting, { nullSafeComparator } from "@plannel/components/aggrid/DefaultGridSetting";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import { AddButton, RemoveButton, SaveButton, FilterButton } from "@plannel/components/ActionIconButton";
import reduxUtil from "@plannel/utils/redux-util";
import debug from "@plannel/utils/debug-util";
import dateUtils from "@plannel/utils/date-util";
import ColorUtil from "../utils/color-util";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import locationService from "@plannel/services/data/location-service";
import GridDatePicker from "@plannel/components/GridDatePicker";
import NumericEditor from "@plannel/components/aggrid/NumericEditor";
import CodeUtil from "@plannel/pages/utils/code-util";
import DateCondition from "@plannel/components/DateCondition";
import NumberUtil from "@plannel/utils/number-util";
import AdvancedFilter from "@plannel/components/filter/AdvancedFilter";

import ResizableFilterPanel from "@plannel/components/filter/ResizableFilterPanel";
const { snackBarMessageColor } = ColorUtil;
const PurchaseOrderMaster = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();

  const pageSizes = [100, 200, 500];
  const [currentPage, setCurrentPage] = useState(reduxViewState ? reduxViewState.currentPage : 1);
  const [tPages, setTotalPages] = useState(0);
  const [pageSize, setPageSize] = useState(reduxViewState ? reduxViewState.pageSize : pageSizes[0]);

  const [sortParams, setSortParams] = useState({});
  const [locationSearch, setLocationSearch] = useState(reduxViewState?.locationSearch ?? { originLocation: null, destLocation: null });
  const itemSearch = useRef(reduxViewState?.itemSearch ?? "");
  const extraItemCodesRef = useRef(reduxViewState?.extraItemCodes ?? []);
  const [advancedFilters, setAdvancedFilters] = useState(reduxViewState?.advancedFilters ? reduxViewState.advancedFilters : null);
  const [openFilter, setOpenFilter] = useState(false);
  const [advancedFilterColor, setAdvancedFilterColor] = useState(!isEmpty(reduxViewState?.advancedFilters) ? "info" : "default");

  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });

  const gridRef = useRef();
  const [gridLoading, setGridLoading] = useState(false);

  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [orders, setOrders] = useState([]);
  const [originLocations, setOriginLocations] = useState([]);
  const [destLocations, setDestLocations] = useState([]);
  const [locations, setLocations] = useState([]);

  const { shipmentCondition } = CodeUtil;

  const CURRENT_DATE = new ZDate();
  const MIN_DATE = new ZDate(CURRENT_DATE.getFullYear() - 10, 0, 1);
  const MAX_DATE = new ZDate(CURRENT_DATE.getFullYear() + 10, 0, 1);
  const START_DATE = new ZDate(CURRENT_DATE.getFullYear(), CURRENT_DATE.getMonth(), 1);
  const END_DATE = new ZDate(CURRENT_DATE.getFullYear(), CURRENT_DATE.getMonth() + 3, 0);
  const initPeriod = { start: reduxViewState?.period ? new ZDate(reduxViewState.period.start): START_DATE, end: reduxViewState?.period ?  new ZDate(reduxViewState.period.end) : END_DATE }
  const [period, setPeriod] =  useState(initPeriod);
  const [selectedSearch, setSelectedSearch] = useState(reduxViewState?.selectedSearch ?? "eta");

  const userData = JSON.parse(localStorage.getItem("user"));
  const currencyMap = useRef({});
  const EXCEL_EXPORT = "excelExport";


  const defaultGridMemo = useMemo(() => {
    return DefaultGridSetting({ title, viewName })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const currencyRenderer = (param, isExport = false) => {
    const locCd = param?.data?.vendorLocation?.locCd;
    let symbol = userData?.currencySymbol;
    if (size(Object.keys(currencyMap?.current))) {
      const currencyCd = currencyMap.current[locCd];
      if (currencyCd) {
        symbol = CodeUtil.getCurrencySymbol(currencyCd);
      }
    }

    const value = param?.data?.[param?.colDef?.field];
    if (isNull(value) || isUndefined(value)) {
      return "";
    }

    const formatValue = NumberUtil.formatNumber(value, { maximumFractionDigits: 2, minimumFractionDigits: 2 });
    if (isExport) {
      return formatValue.replace(/,/g, "");
    }
    if (value < 0) {
      return `-${symbol}${replace(formatValue, "-", "")}`;
    }
    return symbol + formatValue;
  };
  currencyRenderer.customType = EXCEL_EXPORT;

  const valueFormatter = (param) => {
    if (param.column?.colDef) {
      const { colId } = param.column.getColDef();
      if (colId === "originLocationCd" || colId === "destLocationCd") {
        return param.value?.locCd ? param.value.locCd : "";
      } else if (colId === "originLocationName" || colId === "destLocationName") {
        return param.value?.name ? param.value.name : "";
      }
    }

    return param.value?.name ? param.value.name : "";
  }

  const objectComparator = (value1, value2, property, isDescending) => {
    const val1 = !value1 ? "" : value1[property];
    const val2 = !value2 ? "" : value2[property];
    
    return nullSafeComparator(val1, val2, isDescending);
  }

  const columnDefs = [
    {
      headerName: "orderNum", field: "orderNum", width: 150, filterType: 'string'
    },
    { headerName: "vendorCd", field: "vendorCd", width: 150, cellClass: 'stringType', filterType: 'string' },
    { headerName: "vendorName", field: "vendorName", width: 150, cellClass: 'stringType', filterType: 'string' },
    {
      headerName: "originLocation", groupId: "originLocation",
      children: [
        {

          headerName: "locCd", field: "vendorLocation", colId: "originLocationCd",
          type: ['nonEditableColumn'],
          valueFormatter,
          cellClass: 'stringType',
          comparator: (value1, value2, nodeA, nodeB, isDescending) => objectComparator(value1, value2, "locCd", isDescending),
          width: 200, filterType: 'string'
        },
        {
          headerName: "locName", field: "vendorLocation", colId: "originLocationName",
          cellEditor: "agRichSelectCellEditor",
          cellEditorPopup: true,
          type: ["defaultValueParser"],
          valueFormatter,
          cellEditorParams: {
            values: originLocations
          },
          comparator: (value1, value2, nodeA, nodeB, isDescending) => objectComparator(value1, value2, "name", isDescending),
          width: 200, filterType: 'string'
        }
      ]
    },
    {
      headerName: "destLocation", groupId: "destLocation",
      children: [
        {
          headerName: "locCd", field: "destLocation", colId: "destLocationCd",
          type: ['nonEditableColumn'],
          valueFormatter,
          cellClass: 'stringType',
          comparator: (value1, value2, nodeA, nodeB, isDescending) => objectComparator(value1, value2, "locCd", isDescending),
          width: 200, filterType: 'string'
        },
        {
          headerName: "locName", field: "destLocation", colId: "destLocationName",
          cellEditor: "agRichSelectCellEditor",
          cellEditorPopup: true,
          type: ["defaultValueParser"],
          valueFormatter,
          cellEditorParams: {
            values: destLocations
          },
          comparator: (value1, value2, nodeA, nodeB, isDescending) => objectComparator(value1, value2, "name", isDescending),
          width: 200, filterType: 'string'
        }
      ]
    },
    {
      headerName: "item", groupId: "item",
      children: [
        { headerName: "itemCd", field: "itemCd", width: 150, cellClass: 'stringType', filterType: 'string' },
        { headerName: "itemName", field: "itemName", type: ["nonEditableColumn"], width: 280, filterType: 'string' },
      ]
    },
    { headerName: "orderQty", field: "orderQty", type: ["numberColumn", "rightAligned"], width: 130, cellEditor: NumericEditor, filterType: 'number' },
    { headerName: "orderAmt", field: "orderAmt", type: ["numberColumn", "rightAligned"], width: 130, cellEditor: NumericEditor, filterType: 'number' },
    { headerName: "dueDate", field: "dueDate", type: ["defaultValueParser"], cellEditor: GridDatePicker, cellEditorParams: { dateType: "day" }, width: 150, filterType: 'date', valueFormatter: (data) => dateUtils.formatDate(data.value, "yyyy-MM-dd")},
    { headerName: "etd", field: "etd", type: ["defaultValueParser"], cellEditor: GridDatePicker, cellEditorParams: { dateType: "day" }, width: 150, filterType: 'date', valueFormatter: (data) => dateUtils.formatDate(data.value, "yyyy-MM-dd")},
    { headerName: "atd", field: "atd", type: ["defaultValueParser"], cellEditor: GridDatePicker, cellEditorParams: { dateType: "day" }, width: 150, filterType: 'date', valueFormatter: (data) => dateUtils.formatDate(data.value, "yyyy-MM-dd")},
    { headerName: "eta", field: "eta", type: ["defaultValueParser"], cellEditor: GridDatePicker, cellEditorParams: { dateType: "day" }, width: 150, filterType: 'date', valueFormatter: (data) => dateUtils.formatDate(data.value, "yyyy-MM-dd")},
    { headerName: "ata", field: "ata", type: ["defaultValueParser"], cellEditor: GridDatePicker, cellEditorParams: { dateType: "day" }, width: 150, filterType: 'date', valueFormatter: (data) => dateUtils.formatDate(data.value, "yyyy-MM-dd")},
    { headerName: "descTxt", field: "description", width: 400, flex: 1, minWidth: 300, filterType: 'string' },
    { headerName: "createdTs", field: "createdTs", valueFormatter: dateUtils.formatDateTime, type: "nonEditableColumn", filterType: 'timestamp' },
    { headerName: "createdBy", field: "createdUser", colId: "createdBy", type: "nonEditableColumn", width: 150, filterType: 'string' },
    { headerName: "updatedTs", field: "updatedTs", valueFormatter: dateUtils.formatDateTime, type: "nonEditableColumn", filterType: 'timestamp' },
    { headerName: "updatedBy", field: "updatedUser", colId: "updatedBy", type: "nonEditableColumn", width: 150, filterType: 'string' },
  ];

  const getRequestParams = () => {
    const params = {};
    const searchFilters = {};

    if (locationSearch?.originLocation) {
      searchFilters.originLocCd = locationSearch.originLocation.locCd;
    }

    if (locationSearch?.destLocation) {
      searchFilters.destLocCd = locationSearch.destLocation.locCd;
    }

    if (extraItemCodesRef.current?.length > 0) {
      searchFilters.items = [itemSearch.current, ...extraItemCodesRef.current].filter(Boolean);
    } else if (itemSearch.current) {
      searchFilters.item = itemSearch.current;
    }

    if (selectedSearch) {
      searchFilters.condition = selectedSearch
    }

    if (period?.start) {
      searchFilters.startDate = period?.start
    }

    if (period?.end) {
      searchFilters.endDate = period?.end
    }

    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
      params.advancedFilters = advancedFilters;
    }

    if (currentPage) {
      params.page = currentPage - 1;
    }

    if (pageSize) {
      params.pageSize = pageSize;
    }

    params.searchFilters = searchFilters;

    return { ...params, ...sortParams };
  }

  const retrievePurchaseOrder = () => {
    const params = getRequestParams();
    purchaseOrderService.getAll(params)
      .then(response => {
        const { results, totalPages, currency } = response.data;
        if (isEmpty(results)) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
        }
        if (!isEmpty(currency)) {
          currencyMap.current = currency;
        }
        setPurchaseOrders(results);
        setTotalPages(totalPages);
      })
      .catch(e => {
        debug(e);
      })
      .then(() => {
        const reduxState = reduxUtil.createObjectState(viewName, { period, selectedSearch, itemSearch: itemSearch.current, extraItemCodes: extraItemCodesRef.current, currentPage, pageSize, advancedFilters });
        reduxDispatch(reduxUtil.updateViewState(reduxState));
        DataState.initialize(gridRef.current?.api);
      })
  }

  const retrieveLocation = () => {
    locationService.getAll({ pagination: false })
      .then(response => {
        const { results } = response.data;
        setLocations(results);
      })
      .catch(e => {
        debug(e);
      });
  }

  const retrieveBOD = () => {
    bodService.getLookup()
      .then(response => {
        const { results } = response.data;
        setOrders(results);

        let originLocationList = [];
        let destLocationList = [];

        for (const result of results) {
          if (!some(originLocationList, result.originLocation)) {
            if (isEqual(result.originLocation?.locType, CodeUtil.locationType.VENDOR.cd)) {
              originLocationList.push(result.originLocation);
            }
          }

          if (!some(destLocationList, result.destLocation)) {
            destLocationList.push(result.destLocation);
          }
        }

        originLocationList = orderBy(originLocationList, ["name"], ["asc"]);
        destLocationList = orderBy(destLocationList, ["name"], ["asc"]);

        setOriginLocations(originLocationList);
        setDestLocations(destLocationList);
      })
      .catch(e => {
        debug(e);
      })
  }

  const onGridReady = params => {
    retrieveLocation();
    retrieveBOD();

    setGridLoading(true);
    GridUtils.setColumnDefs({ ...params, columnDefs, viewName, initState: true });
  }

  const insertRow = () => {
    const newRow = {
      id: null,
      originLocation: null,
      vendorLocation: null,
      vendorCd: null,
      vendorName: null,
      itemCd: null,
      itemName: null,
      activeFlg: true,
      verNum: null
    };

    DataState.setDataState(GridUtils.addRow(gridRef.current.api, newRow));
  }

  const removePurchaseOrder = () => {
    const PurchaseOrderIds = [];

    gridRef.current.api.stopEditing();
    gridRef.current.api.getSelectedRows().forEach(
      (data) => {
        PurchaseOrderIds.push(data.id)
      }
    );

    if (PurchaseOrderIds.length > 0) {
      purchaseOrderService.remove(PurchaseOrderIds)
        .then(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_DeleteSuccess") });
        })
        .catch(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_DeleteFailed") });
        })
        .then(() => {
          retrievePurchaseOrder();
        });
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
    }
  }

  const savePurchaseOrder = () => {
    gridRef.current.api.stopEditing();
    const allStateData = DataState.getAllStateData(gridRef.current.api);

    if (allStateData.length > 0) {
      purchaseOrderService.upsert(allStateData)
        .then(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
        })
        .catch(e => {
          const { message } = e.response?.data;
          if (message) {
            setSnackInfo({ open: true, severity: message.type, content: t(message.message) });
          } else {
            setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
          }
          debug(e);
        })
        .then(() => {
          retrievePurchaseOrder();
        });
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
    }
  }

  const handleDialog = (action) => {
    if (action === "SAVE") {
      savePurchaseOrder();
    } else if (action === "DELETE") {
      removePurchaseOrder();
    }
  }

  const initPageToRetrieve = () => {
    if (currentPage > 1) {
      setCurrentPage(1);
    } else {
      retrievePurchaseOrder();
    }
  }

  const resetDestLocations = (originLocation) => {
    if (originLocation) {
      const destLocationList = [];

      for (const order of orders) {
        if (order.originLocation.id === originLocation.id) {
          destLocationList.push(order.destLocation);
        }
      }

      setDestLocations(destLocationList);
    }
  }

  const onCellEditingStopped = (params) => {
    if (params.column.colId === "originLocationName") {
      resetDestLocations(params.data.vendorLocation);
    }
  }

  const onSelectionChanged = (params) => {
    resetDestLocations(params.api.getSelectedNodes()[0]?.data?.vendorLocation);
  }

  const collapseFilter = _event => {
    setOpenFilter(!openFilter);
  };

  useEffect(() => {
    if (gridLoading) {
      DataState.initialize(gridRef.current?.api);
      retrievePurchaseOrder();
    }

    return () => {
      setPurchaseOrders([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, gridLoading, sortParams]);

  useEffect(() => {
    if (gridLoading) {
      initPageToRetrieve();
    }

    return () => {
      setPurchaseOrders([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationSearch.originLocation?.id, locationSearch.destLocation?.id, advancedFilters, period, selectedSearch]);

  useEffect(() => {
    if (!isEmpty(originLocations) || !isEmpty(destLocations)) {
      GridUtils.setColumnDefs({ ...gridRef.current, columnDefs });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [originLocations, destLocations]);

  useEffect(() => {
    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
      setAdvancedFilterColor("info");
    } else {
      setAdvancedFilterColor("default");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedFilters]);

  return (
    <>
      <FilterContainer>
        <DateCondition
          conditionList={shipmentCondition}
          period={period}
          maxDate={MAX_DATE}
          minDate={MIN_DATE}
          selectedSearch={selectedSearch}
          setSelectedSearch={setSelectedSearch}
          setPeriod={setPeriod}
          useAutoComplete={false}
          views={['year', 'month']}
        />
        <Autocomplete
          options={locations}
          getOptionLabel={option => option.name}
          value={locationSearch.originLocation}
          onChange={(_, newValue) => setLocationSearch({ ...locationSearch, originLocation: newValue })}
          isOptionEqualToValue={(option, value) => option.locCd === value.locCd}
          size="small"
          sx={{ minWidth: 240, mr: 1 }}
          renderInput={params => (
            <TextField
              {...params}
              label={t("originLocation")}
            />
          )}
        />
        <Autocomplete
          options={locations}
          getOptionLabel={option => option.name}
          value={locationSearch.destLocation}
          onChange={(_, newValue) => setLocationSearch({ ...locationSearch, destLocation: newValue })}
          isOptionEqualToValue={(option, value) => option.locCd === value.locCd}
          size="small"
          sx={{ mr: 1, minWidth: 240 }}
          renderInput={params => (
            <TextField
              {...params}
              label={t("destLocation")}
            />
          )}
        />
        <ItemAutocomplete retrieve={initPageToRetrieve} searchValue={itemSearch} useAccessFilter={false} extraCodesRef={extraItemCodesRef} />
        <Box sx={{ ml: "auto" }}>
          <ExcelExportButton title={title} service={purchaseOrderService} columnDefs={columnDefs}
            params={getRequestParams()} />
          <AddButton onClick={insertRow} />
          <RemoveButton onClick={() => setDialogInfo({ open: true, title: "delete", content: t("MSG_DeleteConfirm") })} />
          <SaveButton onClick={() => setDialogInfo({ open: true, title: "save", content: t("MSG_SaveConfirm") })} />
          <FilterButton label="advancedFilter" advancedFilterColor={advancedFilterColor} onClick={collapseFilter} />
        </Box>
      </FilterContainer>
      <Box className="ag-theme-balham grid-area" mb={1}>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'row', height: '100%', mr: 1.5 }}>
          <Box sx={{ flexGrow: 1 }}>
            <AgGridReact
              ref={gridRef}
              rowData={purchaseOrders}
              {...defaultGridMemo}
              rowSelection={{
                ...defaultGridMemo.rowSelection,
                headerCheckbox: true,
                checkboxes: true,
              }}
              onGridReady={onGridReady}
              onSortChanged={params => setSortParams(GridUtils.getSortState(params))}
              onCellEditingStopped={onCellEditingStopped}
              onSelectionChanged={onSelectionChanged}
            />
          </Box>
          {openFilter && (
            <ResizableFilterPanel>
              <AdvancedFilter
                columnDefs={columnDefs}
                advancedFilters={advancedFilters}
                onApply={setAdvancedFilters}
                onClose={() => setOpenFilter(false)}
              />
            </ResizableFilterPanel>
          )}
        </Box>
      </Box>
      <PaginationContainer
        currentPage={currentPage} totalPages={tPages}
        pageSize={pageSize} pageSizes={pageSizes}
        setCurrentPage={setCurrentPage} setPageSize={setPageSize}
      />
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

export default withTranslation()(PurchaseOrderMaster);